// Колоните на „Отстъпки и надбавки" — КЛИЕНТСКИ режим:
// `GET /reports/analytics/discounts` връща целия период наведнъж, затова
// сортирането, филтрите в колоните и редът „Общо" се смятат в браузъра.
//
// Видът на корекцията НЕ е акцент: „Вид" е обикновена текстова колона с
// чеклист филтър, като всяка друга — нито цветен бадж на реда, нито отделен
// селект над таблицата.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { AdjustmentAmount } from "./AdjustmentAmount";
import type { NamedDiscountRow } from "./discountsMath";

const helper = createColumnHelper<NamedDiscountRow>();

export const useDiscountsColumns = (isMultiDay: boolean) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor((row) => row.closed_at, {
        id: "closed_at",
        header: t("reportsPage.discountsTime"),
        size: 104,
        meta: {
          // Датата под часа само при многодневен период — иначе повтаря
          // избраното в лентата горе на всеки ред.
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "never" },
          excel: { format: "date" },
        },
      }),

      helper.accessor((row) => row.billLabel, {
        id: "bill",
        header: t("reportsPage.discountsBillId"),
        size: 96,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.waiterLabel, {
        id: "waiter",
        header: t("reportsPage.waiter"),
        size: 150,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist" },
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.clientLabel, {
        id: "client",
        header: t("reportsPage.discountsClient"),
        size: 150,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // Броят гости по сметката (`bills.guests`); невъведеният е тире.
      // `undefined`, не `null`: филтърът „от–до" чете `Number(null)` като 0 и
      // би пуснал невъведените при „до N"; `Number(undefined)` ги изключва.
      helper.accessor((row) => row.guests ?? undefined, {
        id: "guests",
        header: t("reportsPage.guestsCount"),
        size: 96,
        meta: {
          cell: { kind: "num" },
          filter: { kind: "numberRange" },
          total: "sum",
          align: "right",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.kindLabel, {
        id: "kind",
        header: t("reportsPage.discountsKind"),
        size: 112,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist" },
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.typeLabel, {
        id: "discount_type",
        header: t("reportsPage.discountsType"),
        size: 150,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // Без знак: посоката я казва колоната „Вид", а сортирането по абсолютна
      // стойност отговаря на въпроса „кой е дал най-голямата корекция".
      helper.accessor((row) => row.percentAbs, {
        id: "percent",
        header: t("reportsPage.discountsPercent"),
        size: 100,
        meta: {
          // клиентският процент е въведен от човек — „0 %"/„10 %", не „0.0 %"
          cell: { kind: "pct", trim: true },
          filter: { kind: "numberRange" },
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.amountBefore, {
        id: "before",
        header: t("reportsPage.discountsBefore"),
        size: 116,
        meta: {
          cell: { kind: "money" },
          total: "sum",
          hideBelow: "narrow",
          excel: { format: "money" },
        },
      }),

      // Колоната носи И ДВЕТЕ посоки: отстъпката е отрицателна, надбавката —
      // положителна (`adjustment = −total_discount`). Затова заглавието казва
      // и двете: сборът в реда „Общо" е НЕТО корекция, а не „дадени отстъпки" —
      // последното стои в кутийката горе и се смята само от отрицателните.
      helper.accessor((row) => row.adjustment, {
        id: "adjustment",
        header: t("reportsPage.discountsAdjustmentColumn"),
        size: 138,
        cell: (context) => <AdjustmentAmount value={context.getValue()} />,
        meta: {
          label: t("reportsPage.discountsAdjustmentColumn"),
          // Колоната има собствен рендер, затова тоталът се описва отделно.
          totalCell: { kind: "money" },
          total: "sum",
          align: "right",
          alwaysVisible: true,
          excel: {
            header: t("reportsPage.discountsAdjustmentColumn"),
            format: "money",
          },
        },
      }),

      // Само надбавките, като положително число — сборът им иначе се вижда
      // единствено в кутийката горе. Изгасена по подразбиране: надбавките са
      // рядкост и не бива да заемат ред на всеки екран.
      helper.accessor((row) => (row.adjustment > 0 ? row.adjustment : 0), {
        id: "surcharge",
        header: t("reportsPage.surchargesKicker"),
        size: 118,
        meta: {
          cell: { kind: "money", mutedZero: true },
          total: "sum",
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.paid, {
        id: "paid",
        header: t("reportsPage.discountsPaid"),
        size: 122,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),
    ],
    [t, isMultiDay],
  );
};
